import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, FilterBar, PageHeader, SplitDetailPanel, StatusBadge } from '../components/ui';
import { mapStatusToBadge } from '../lib/status';

type ConsumptionStatus = 'consumed' | 'suspicious' | 'blocked' | 'expired';

interface ConsumptionEvent {
  id: string;
  timestamp: string;
  productName: string;
  batchId: string;
  qrId: string;
  status: ConsumptionStatus;
  quantity: number;
  location: string;
  region: string;
  fulfillmentNode: string;
  actorType: 'Consumer' | 'Retail Partner' | 'Distributor';
  deviceHash: string;
  anchorRef: string;
  riskScore: number;
}

const consumptionEvents: ConsumptionEvent[] = [
  {
    id: 'CON-20260220-001',
    timestamp: '2026-02-20T07:58:00Z',
    productName: 'Pure Ghee 500ml',
    batchId: 'BT-2026-011',
    qrId: 'QR-881A-90CC',
    status: 'consumed',
    quantity: 1,
    location: 'Dallas, US',
    region: 'Texas',
    fulfillmentNode: 'Dallas Hub 2',
    actorType: 'Consumer',
    deviceHash: 'DEV-07f9ac-9a31',
    anchorRef: '0x3b108a...c910',
    riskScore: 9,
  },
  {
    id: 'CON-20260220-002',
    timestamp: '2026-02-20T08:24:00Z',
    productName: 'Tea Tree Soap Pack',
    batchId: 'BT-2026-008',
    qrId: 'QR-9D2B-774A',
    status: 'consumed',
    quantity: 2,
    location: 'Phoenix, US',
    region: 'Arizona',
    fulfillmentNode: 'Phoenix North',
    actorType: 'Retail Partner',
    deviceHash: 'DEV-11cd7e-f200',
    anchorRef: '0x9fe224...ad00',
    riskScore: 14,
  },
  {
    id: 'CON-20260220-003',
    timestamp: '2026-02-20T08:52:00Z',
    productName: 'Herbal Shampoo 200ml',
    batchId: 'BT-2026-006',
    qrId: 'QR-6CC3-4A81',
    status: 'suspicious',
    quantity: 6,
    location: 'Houston, US',
    region: 'Texas',
    fulfillmentNode: 'Houston East',
    actorType: 'Distributor',
    deviceHash: 'DEV-aaf220-11de',
    anchorRef: '0x8ad115...d311',
    riskScore: 72,
  },
  {
    id: 'CON-20260220-004',
    timestamp: '2026-02-20T09:13:00Z',
    productName: 'Pure Ghee 1L',
    batchId: 'BT-2026-011',
    qrId: 'QR-11E1-8EE0',
    status: 'blocked',
    quantity: 4,
    location: 'San Jose, US',
    region: 'California',
    fulfillmentNode: 'Bay Area Central',
    actorType: 'Retail Partner',
    deviceHash: 'DEV-2a7cd0-4bd1',
    anchorRef: '0x7bb881...fd12',
    riskScore: 88,
  },
  {
    id: 'CON-20260220-005',
    timestamp: '2026-02-20T09:50:00Z',
    productName: 'Tea Tree Soap Pack',
    batchId: 'BT-2026-008',
    qrId: 'QR-CC28-0443',
    status: 'expired',
    quantity: 1,
    location: 'Denver, US',
    region: 'Colorado',
    fulfillmentNode: 'Denver South',
    actorType: 'Consumer',
    deviceHash: 'DEV-1b0cee-8280',
    anchorRef: '0x2fd121...5cb1',
    riskScore: 41,
  },
  {
    id: 'CON-20260220-006',
    timestamp: '2026-02-20T10:28:00Z',
    productName: 'Herbal Shampoo 200ml',
    batchId: 'BT-2026-006',
    qrId: 'QR-4AB7-11D3',
    status: 'consumed',
    quantity: 3,
    location: 'Austin, US',
    region: 'Texas',
    fulfillmentNode: 'Austin Core',
    actorType: 'Distributor',
    deviceHash: 'DEV-d8f300-ff31',
    anchorRef: '0x0dd2ea...ac88',
    riskScore: 19,
  },
];

const toDateTime = (value: string) =>
  new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const riskToneFromScore = (riskScore: number): 'active' | 'suspicious' | 'recalled' => {
  if (riskScore >= 80) {
    return 'recalled';
  }
  if (riskScore >= 50) {
    return 'suspicious';
  }
  return 'active';
};

export default function ConsumptionLogsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'latest' | 'earliest' | 'risk-high' | 'quantity-high'>('latest');
  const [selectedId, setSelectedId] = useState<string>(consumptionEvents[0]?.id ?? '');

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'All', count: consumptionEvents.length },
      ...(['consumed', 'suspicious', 'blocked', 'expired'] as ConsumptionStatus[]).map((status) => ({
        value: status,
        label: mapStatusToBadge(status).label,
        count: consumptionEvents.filter((event) => event.status === status).length,
      })),
    ],
    []
  );

  const filteredEvents = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = consumptionEvents.filter((event) => {
      const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
      const matchesSearch =
        normalizedSearch === '' ||
        event.productName.toLowerCase().includes(normalizedSearch) ||
        event.batchId.toLowerCase().includes(normalizedSearch) ||
        event.qrId.toLowerCase().includes(normalizedSearch) ||
        event.region.toLowerCase().includes(normalizedSearch);
      return matchesStatus && matchesSearch;
    });

    return filtered.sort((a, b) => {
      if (sortBy === 'earliest') {
        return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      }
      if (sortBy === 'risk-high') {
        return b.riskScore - a.riskScore;
      }
      if (sortBy === 'quantity-high') {
        return b.quantity - a.quantity;
      }
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  }, [searchTerm, statusFilter, sortBy]);

  useEffect(() => {
    if (filteredEvents.length === 0) {
      setSelectedId('');
      return;
    }

    if (!filteredEvents.some((event) => event.id === selectedId)) {
      setSelectedId(filteredEvents[0].id);
    }
  }, [filteredEvents, selectedId]);

  const selectedEvent = filteredEvents.find((event) => event.id === selectedId);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <PageHeader
        title="Consumption Logs"
        description="Monitor consumption events and fulfillment traces across product lifecycle nodes."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(isAdminRoute ? '/admin' : '/manufacturer')}
            >
              Back to Dashboard
            </Button>
            <Button variant="outline">Export CSV</Button>
          </div>
        }
      />

      <FilterBar
        className="mt-6"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by product, batch, QR, or region..."
        statusOptions={statusOptions}
        selectedStatus={statusFilter}
        onStatusChange={setStatusFilter}
        sortOptions={[
          { value: 'latest', label: 'Latest First' },
          { value: 'earliest', label: 'Earliest First' },
          { value: 'risk-high', label: 'Risk (High to Low)' },
          { value: 'quantity-high', label: 'Quantity (High to Low)' },
        ]}
        selectedSort={sortBy}
        onSortChange={(value) => setSortBy(value as 'latest' | 'earliest' | 'risk-high' | 'quantity-high')}
      />

      <SplitDetailPanel
        className="mt-4"
        listTitle={`Events (${filteredEvents.length})`}
        detailTitle={selectedEvent ? `Event ${selectedEvent.id}` : 'Event Details'}
        list={
          filteredEvents.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">No consumption events match the current filters.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredEvents.map((event) => {
                const status = mapStatusToBadge(event.status);
                const active = selectedId === event.id;
                return (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => setSelectedId(event.id)}
                    className={`w-full px-4 py-3 text-left transition-colors ${
                      active ? 'bg-emerald-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{event.productName}</p>
                        <p className="text-xs text-gray-500">{event.fulfillmentNode}</p>
                      </div>
                      <StatusBadge tone={status.tone} size="sm">
                        {status.label}
                      </StatusBadge>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                      <span>{toDateTime(event.timestamp)}</span>
                      <span>Qty {event.quantity}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )
        }
        detail={
          selectedEvent ? (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge {...mapStatusToBadge(selectedEvent.status)} />
                <StatusBadge tone={riskToneFromScore(selectedEvent.riskScore)}>
                  Risk {selectedEvent.riskScore}
                </StatusBadge>
                <StatusBadge tone="anchored">{selectedEvent.actorType}</StatusBadge>
              </div>

              <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">Product</dt>
                  <dd className="mt-1 font-medium text-gray-900">{selectedEvent.productName}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">QR ID</dt>
                  <dd className="mt-1 font-medium text-gray-900">{selectedEvent.qrId}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">Batch</dt>
                  <dd className="mt-1 font-medium text-gray-900">{selectedEvent.batchId}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">Timestamp</dt>
                  <dd className="mt-1 font-medium text-gray-900">{toDateTime(selectedEvent.timestamp)}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">Quantity</dt>
                  <dd className="mt-1 font-medium text-gray-900">{selectedEvent.quantity}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">Location</dt>
                  <dd className="mt-1 font-medium text-gray-900">{selectedEvent.location}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">Region</dt>
                  <dd className="mt-1 font-medium text-gray-900">{selectedEvent.region}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">Fulfillment Node</dt>
                  <dd className="mt-1 font-medium text-gray-900">{selectedEvent.fulfillmentNode}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <dt className="text-xs uppercase text-gray-500">Device Hash</dt>
                  <dd className="mt-1 font-mono text-xs text-gray-900">{selectedEvent.deviceHash}</dd>
                </div>
                <div className="rounded-lg bg-gray-50 p-3 md:col-span-2">
                  <dt className="text-xs uppercase text-gray-500">Blockchain Anchor</dt>
                  <dd className="mt-1 font-mono text-xs text-gray-900">{selectedEvent.anchorRef}</dd>
                </div>
              </dl>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Select an event from the list to view details.</p>
          )
        }
      />
    </div>
  );
}
